import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import {
  ArrowRight,
  CircleCheck,
  FileText,
  Landmark,
  NotebookPen,
  Receipt,
  TrendingDown,
  TrendingUp,
  Upload,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BusinessHome } from '@/components/portal/business-home';
import { MONEYBAR } from '@/components/sales/money-bar';
import { useBankAccounts } from '@/lib/banking';
import { useMe } from '@/lib/clients';
import {
  DASHBOARD_PERIODS,
  PREVIOUS_LABEL,
  barWidths,
  currentVatReturn,
  dashboardRange,
  isDashboardPeriod,
  linePoints,
  minorOf,
  monthLabel,
  percentChange,
  taskView,
  useDashboard,
  visibleTasks,
  type DashboardData,
  type DashboardPeriod,
} from '@/lib/dashboard';
import { todayIso } from '@/lib/dates';
import { minorToPlain } from '@/lib/money';
import { formatAmount, formatDate, formatMoney } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { useBusinessView } from '@/lib/portal';
import { useSalesSummary } from '@/lib/sales';
import { useCompanySettings } from '@/lib/settings';
import { cn } from '@/lib/utils';
import { TONE_CLASS, useVatReturns, vatStatusChip } from '@/lib/vat';
import { monthLabel as gstMonthLabel, useGstMonths } from '@/lib/gst';
import { homeCurrency, isIndia } from '@/lib/org-locale';

export const Route = createFileRoute('/dashboard')({
  component: Home,
  validateSearch: (s: Record<string, unknown>): { period?: DashboardPeriod } =>
    isDashboardPeriod(s['period']) ? { period: s['period'] } : {},
});

/** Client users (and accounting staff previewing the Business view) get the Business view home. */
function Home() {
  return useBusinessView().on ? <BusinessHome /> : <Dashboard />;
}

// chart colours come from the design tokens (index.css)
const BRAND = 'var(--chart-1)';
const PREVIOUS = 'var(--chart-1-previous)';

/** Dashboard › Business overview (document 06 D1): one card per area, each linking to its screen. */
function Dashboard() {
  const orgId = useOrgId();
  const { period = 'this_month' } = Route.useSearch();
  const navigate = useNavigate();
  const company = useCompanySettings();
  const me = useMe();
  const today = todayIso();
  const fyStartMonth = company.data?.advanced.fiscalYearStart ?? 1;
  const ready = company.isSuccess || company.isError;
  const range = ready ? dashboardRange(period, today, fyStartMonth) : null;
  const dash = useDashboard(range);

  if (!orgId)
    return (
      <>
        <PageHeader title="Dashboard" description="Business overview" />
        <EmptyState title="Select a client" hint="Choose a client to see its business overview." />
      </>
    );

  return (
    <>
      <PageHeader
        title={me.data?.name ? `${greeting()}, ${me.data.name.split(' ')[0]}` : 'Dashboard'}
        description={
          company.data ? `Business overview · ${company.data.company.name}` : 'Business overview'
        }
        actions={
          <NativeSelect
            aria-label="Dashboard period"
            className="w-[200px] bg-card"
            value={period}
            onChange={(e) =>
              void navigate({
                to: '/dashboard',
                search: e.target.value === 'this_month' ? {} : { period: e.target.value as never },
              })
            }
          >
            {DASHBOARD_PERIODS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </NativeSelect>
        }
      />
      {dash.isError ? (
        <EmptyState title="Could not load the dashboard" hint={dash.error.message} />
      ) : null}
      <KpiStrip data={dash.data} />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="dashboard">
        <ProfitAndLossCard data={dash.data} period={period} />
        <ExpensesCard data={dash.data} />
        <InvoicesCard />
        <BankAccountsCard />
        <SalesTrendCard data={dash.data} />
        {isIndia() ? (
          <GstCard />
        ) : (
          <VatCard today={today} vatRegistered={company.data?.vat.vatRegistered ?? true} />
        )}
        <TasksCard data={dash.data} isStaff={me.data?.isStaff ?? true} />
        <QuickActionsCard />
      </div>
    </>
  );
}

const greeting = (): string => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

/** Headline figures above the cards, as in Jira's summary tiles. */
function KpiStrip({ data }: { data: DashboardData | undefined }) {
  const sales = useSalesSummary();
  const banks = useBankAccounts();
  const currency = data?.currency ?? homeCurrency();
  const cash = banks.data
    ?.filter((a) => a.accountType === 'bank')
    .reduce((sum, a) => sum + minorOf(a.homeLedgerBalance), 0n);
  const change = data
    ? percentChange(data.profitAndLoss.netProfit, data.profitAndLoss.previous.netProfit)
    : null;
  const forReview = banks.data?.reduce((n, a) => n + a.forReview, 0);
  const tiles: Array<{
    icon: LucideIcon;
    tone: string;
    label: string;
    value: string | undefined;
    hint?: string;
  }> = [
    {
      icon: change !== null && change < 0 ? TrendingDown : TrendingUp,
      tone: 'bg-[#dcfff1] text-success',
      label: 'Net profit',
      value: data ? formatMoney(data.profitAndLoss.netProfit, currency) : undefined,
      ...(change !== null
        ? { hint: `${change > 0 ? '+' : ''}${change.toFixed(1)}% on the previous period` }
        : {}),
    },
    {
      icon: Wallet,
      tone: 'bg-brand-blue-50 text-primary',
      label: 'Cash in bank',
      value: cash !== undefined ? formatMoney(minorToPlain(cash), currency) : undefined,
      ...(banks.data ? { hint: `${banks.data.length} accounts` } : {}),
    },
    {
      icon: FileText,
      tone: 'bg-[#fff3eb] text-[#c25100]',
      label: 'Owed to you',
      value: sales.data ? formatMoney(sales.data.openInvoices.amount, currency) : undefined,
      ...(sales.data
        ? { hint: `${sales.data.overdue.count} overdue · ${sales.data.openInvoices.count} open` }
        : {}),
    },
    {
      icon: Landmark,
      tone: 'bg-[#f3f0ff] text-[#5e4db2]',
      label: 'To review',
      value: forReview !== undefined ? String(forReview) : undefined,
      hint: 'Bank transactions waiting',
    },
  ];
  return (
    <div
      className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
      data-testid="kpi-strip"
    >
      {tiles.map((t) => (
        <div key={t.label} className="flex items-start gap-3 rounded-lg border bg-card p-4">
          <div className={cn('grid size-10 shrink-0 place-items-center rounded-md', t.tone)}>
            <t.icon className="size-5" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-muted-foreground">{t.label}</div>
            {t.value === undefined ? (
              <div className="mt-1 h-6 w-28 animate-pulse rounded bg-muted" />
            ) : (
              <div className="mt-0.5 truncate text-lg font-bold tnum">{t.value}</div>
            )}
            {t.hint ? <div className="text-xs text-muted-foreground">{t.hint}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

const QUICK_ACTIONS: Array<{ icon: LucideIcon; label: string; to: string }> = [
  { icon: FileText, label: 'Create invoice', to: '/sales/invoices/new' },
  { icon: Receipt, label: 'Record expense', to: '/expenses/expenses/new' },
  { icon: Upload, label: 'Import bank statement', to: '/banking/import' },
  { icon: NotebookPen, label: 'Journal entry', to: '/accounting/journal-entries/new' },
];

/** Shortcuts to the everyday forms. */
function QuickActionsCard() {
  return (
    <Card className="gap-3" data-testid="card-quick-actions">
      <CardHeader>
        <CardTitle className="text-base">Quick actions</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2">
        {QUICK_ACTIONS.map((a) => (
          <Link
            key={a.to}
            to={a.to as never}
            className="flex items-center gap-3 rounded-md border px-3 py-2.5 text-sm font-medium transition hover:border-brand-blue-200 hover:bg-accent hover:text-accent-foreground"
          >
            <span className="grid size-7 place-items-center rounded bg-brand-blue-50">
              <a.icon className="size-4 text-primary" />
            </span>
            {a.label}
            <ArrowRight className="ml-auto size-3.5 text-muted-foreground" />
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

function DashCard({
  title,
  to,
  params,
  search,
  linkLabel,
  children,
  testId,
  className,
}: {
  title: string;
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
  linkLabel: string;
  children: ReactNode;
  testId: string;
  className?: string;
}) {
  return (
    <Card className={cn('gap-3', className)} data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <Link
          to={to as never}
          {...(params ? { params: params as never } : {})}
          {...(search ? { search: search as never } : {})}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          {linkLabel}
          <ArrowRight className="size-3" />
        </Link>
      </CardHeader>
      <CardContent className="text-sm">{children}</CardContent>
    </Card>
  );
}

const Loading = () => <div className="h-[140px] animate-pulse rounded-md bg-muted/50" />;

function ProfitAndLossCard({
  data,
  period,
}: {
  data: DashboardData | undefined;
  period: DashboardPeriod;
}) {
  const range = data ? { from: data.period.from, to: data.period.to } : undefined;
  return (
    <DashCard
      title="Profit and loss"
      to="/reports/$reportKey"
      params={{ reportKey: 'profit-and-loss' }}
      linkLabel="View report"
      testId="card-pnl"
      {...(range ? { search: { ...range } } : {})}
    >
      {!data ? <Loading /> : <PnlBody data={data} previousLabel={PREVIOUS_LABEL[period]} />}
    </DashCard>
  );
}

function PnlBody({ data, previousLabel }: { data: DashboardData; previousLabel: string }) {
  const p = data.profitAndLoss;
  const change = percentChange(p.netProfit, p.previous.netProfit);
  const widths = barWidths([p.income, p.previous.income, p.expenses, p.previous.expenses]);
  const rows: Array<{ label: string; now: string; before: string; w: [number, number] }> = [
    { label: 'Income', now: p.income, before: p.previous.income, w: [widths[0]!, widths[1]!] },
    {
      label: 'Expenses',
      now: p.expenses,
      before: p.previous.expenses,
      w: [widths[2]!, widths[3]!],
    },
  ];
  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs text-muted-foreground">
          Net profit · {formatDate(data.period.from)} – {formatDate(data.period.to)}
        </div>
        <div className="tnum text-2xl font-semibold" data-testid="pnl-net">
          {formatMoney(p.netProfit, data.currency)}
        </div>
        <div className="text-xs text-muted-foreground">
          {previousLabel}:{' '}
          <span className="tnum">{formatMoney(p.previous.netProfit, data.currency)}</span>
          {change !== null ? (
            <span className="ml-1">
              ({change > 0 ? '+' : ''}
              {change.toFixed(1)}%)
            </span>
          ) : null}
        </div>
      </div>
      {rows.map((r) => (
        <div key={r.label} className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="font-medium">{r.label}</span>
            <span className="tnum">{formatMoney(r.now, data.currency)}</span>
          </div>
          <Bar
            pct={r.w[0]}
            color={BRAND}
            title={`${r.label}: ${formatMoney(r.now, data.currency)}`}
          />
          <Bar
            pct={r.w[1]}
            color={PREVIOUS}
            title={`${r.label}, ${previousLabel.toLowerCase()}: ${formatMoney(r.before, data.currency)}`}
          />
        </div>
      ))}
      <div className="flex gap-4 text-xs text-muted-foreground">
        <Legend color={BRAND} label="This period" />
        <Legend color={PREVIOUS} label={previousLabel} />
      </div>
    </div>
  );
}

function Bar({ pct, color, title }: { pct: number; color: string; title: string }) {
  return (
    <div className="h-2.5 w-full rounded bg-muted/60" title={title}>
      <div
        className="h-full rounded-r"
        style={{ width: `${Math.max(pct, pct > 0 ? 1 : 0)}%`, background: color }}
      />
    </div>
  );
}

const Legend = ({ color, label }: { color: string; label: string }) => (
  <span className="inline-flex items-center gap-1">
    <span className="inline-block size-2 rounded-full" style={{ background: color }} />
    {label}
  </span>
);

function ExpensesCard({ data }: { data: DashboardData | undefined }) {
  return (
    <DashCard title="Expenses" to="/expenses" linkLabel="View expenses" testId="card-expenses">
      {!data ? (
        <Loading />
      ) : data.expenses.top.length === 0 ? (
        <p className="text-muted-foreground">No expenses in this period.</p>
      ) : (
        <ExpensesBody data={data} />
      )}
    </DashCard>
  );
}

function ExpensesBody({ data }: { data: DashboardData }) {
  const items = [
    ...data.expenses.top.map((x) => ({
      key: x.accountId,
      label: x.name,
      amount: x.amount,
      id: x.accountId,
    })),
    ...(data.expenses.other !== '0.00'
      ? [{ key: 'other', label: 'Everything else', amount: data.expenses.other, id: null }]
      : []),
  ];
  const widths = barWidths(items.map((i) => i.amount));
  return (
    <div className="space-y-2">
      <div>
        <div className="text-xs text-muted-foreground">Total spent</div>
        <div className="tnum text-2xl font-semibold">
          {formatMoney(data.expenses.total, data.currency)}
        </div>
      </div>
      <ul className="space-y-1.5">
        {items.map((i, n) => (
          <li key={i.key} className="space-y-0.5">
            <div className="flex justify-between gap-2 text-xs">
              {i.id ? (
                <Link
                  to="/reports/$reportKey"
                  params={{ reportKey: 'transaction-detail-by-account' }}
                  search={{ accounts: i.id, from: data.period.from, to: data.period.to }}
                  className="truncate text-primary hover:underline"
                >
                  {i.label}
                </Link>
              ) : (
                <span className="truncate">{i.label}</span>
              )}
              <span className="tnum shrink-0">{formatAmount(i.amount)}</span>
            </div>
            <Bar
              pct={widths[n]!}
              color={i.id ? BRAND : 'var(--chart-5)'}
              title={`${i.label}: ${formatMoney(i.amount, data.currency)}`}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function InvoicesCard() {
  const s = useSalesSummary();
  const d = s.data;
  const segs = d
    ? [
        {
          key: 'overdue',
          label: 'Overdue',
          ...d.overdue,
          color: MONEYBAR.overdue,
          status: 'overdue',
        },
        {
          key: 'not_due',
          label: 'Not due yet',
          ...d.notDueYet,
          color: MONEYBAR.notDue,
          status: 'not_due',
        },
        {
          key: 'not_deposited',
          label: 'Not deposited',
          ...d.notDeposited,
          color: MONEYBAR.notDeposited,
          status: null,
        },
        {
          key: 'deposited',
          label: 'Deposited (last 30 days)',
          ...d.depositedLast30,
          color: MONEYBAR.deposited,
          status: null,
        },
      ]
    : [];
  const unpaid = barWidths(segs.slice(0, 2).map((x) => x.amount));
  const paid = barWidths(segs.slice(2).map((x) => x.amount));
  const share = (w: number[], i: number) => {
    const sum = w.reduce((a, b) => a + b, 0);
    return sum ? (w[i]! / sum) * 100 : 0;
  };
  return (
    <DashCard
      title="Invoices"
      to="/sales/invoices"
      linkLabel="View invoices"
      testId="card-invoices"
    >
      {!d ? (
        <Loading />
      ) : (
        <div className="space-y-4">
          {[
            { title: 'Unpaid', items: segs.slice(0, 2), w: unpaid },
            { title: 'Paid', items: segs.slice(2), w: paid },
          ].map((g) => (
            <div key={g.title}>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {g.title}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {g.items.map((x) => (
                  <Link
                    key={x.key}
                    to="/sales/invoices"
                    search={x.status ? { status: x.status as never } : {}}
                    className="rounded-md p-1 hover:bg-muted/50"
                    data-testid={`dash-money-${x.key}`}
                  >
                    <div className="tnum font-semibold">
                      {homeCurrency()} {formatAmount(x.amount)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {x.count} · {x.label}
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-1 flex h-2 overflow-hidden rounded-full bg-muted">
                {g.items.map((x, i) =>
                  share(g.w, i) > 0 ? (
                    <div key={x.key} style={{ width: `${share(g.w, i)}%`, background: x.color }} />
                  ) : null,
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashCard>
  );
}

function BankAccountsCard() {
  const accounts = useBankAccounts();
  return (
    <DashCard title="Bank accounts" to="/banking" linkLabel="Go to banking" testId="card-bank">
      {!accounts.data ? (
        <Loading />
      ) : accounts.data.length === 0 ? (
        <p className="text-muted-foreground">No bank or credit card accounts yet.</p>
      ) : (
        <ul className="divide-y">
          {accounts.data.map((a) => (
            <li key={a.accountId} className="py-2 first:pt-0 last:pb-0">
              <Link
                to="/banking"
                search={{ accountId: a.accountId }}
                className="flex items-baseline justify-between gap-2 font-medium hover:text-primary"
              >
                <span className="truncate">{a.name}</span>
                {a.forReview > 0 ? (
                  <span className="shrink-0 rounded-full bg-primary/10 px-2 text-xs text-primary">
                    {a.forReview} to review
                  </span>
                ) : null}
              </Link>
              <div className="mt-1 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-muted-foreground">
                    Bank balance{a.bankBalanceDate ? ` · ${formatDate(a.bankBalanceDate)}` : ''}
                  </div>
                  <div className="tnum">
                    {a.bankBalance === null ? '—' : formatMoney(a.bankBalance, a.currency)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-muted-foreground">In Yedo Books</div>
                  <div className="tnum">{formatMoney(a.ledgerBalance, a.currency)}</div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </DashCard>
  );
}

function SalesTrendCard({ data }: { data: DashboardData | undefined }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 320;
  const H = 110;
  const trend = data?.salesTrend ?? [];
  const pts = linePoints(
    trend.map((x) => x.income),
    W,
    H,
  );
  const shown = hover ?? trend.length - 1;
  return (
    <DashCard
      title="Sales"
      to="/reports/$reportKey"
      params={{ reportKey: 'profit-and-loss' }}
      linkLabel="View report"
      testId="card-sales"
      {...(data
        ? {
            search: {
              from: trend[0]?.from ?? data.period.from,
              to: trend.at(-1)?.to ?? data.period.to,
              columns: 'month',
            },
          }
        : {})}
    >
      {!data ? (
        <Loading />
      ) : (
        <div>
          <div className="text-xs text-muted-foreground">
            Income · {monthLabel(trend[shown]!.month, true)}
          </div>
          <div className="tnum text-2xl font-semibold">
            {formatMoney(trend[shown]!.income, data.currency)}
          </div>
          <svg
            viewBox={`-14 -6 ${W + 28} ${H + 26}`}
            className="mt-2 w-full"
            role="img"
            aria-label="Income per month for the last twelve months"
            onMouseLeave={() => setHover(null)}
          >
            <line x1={0} x2={W} y1={H} y2={H} stroke="currentColor" className="text-border" />
            <polyline
              fill="none"
              stroke={BRAND}
              strokeWidth={2}
              strokeLinejoin="round"
              points={pts.map((p) => `${p.x},${p.y}`).join(' ')}
            />
            {hover !== null ? (
              <line
                x1={pts[hover]!.x}
                x2={pts[hover]!.x}
                y1={0}
                y2={H}
                stroke="currentColor"
                className="text-muted-foreground"
                strokeDasharray="2 3"
              />
            ) : null}
            {pts.map((p, i) => (
              <g key={trend[i]!.month}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={i === shown ? 4 : 2.5}
                  fill={BRAND}
                  stroke="white"
                  strokeWidth={1.5}
                />
                <rect
                  x={p.x - W / 24}
                  y={-6}
                  width={W / 12}
                  height={H + 12}
                  fill="transparent"
                  onMouseEnter={() => setHover(i)}
                >
                  <title>
                    {monthLabel(trend[i]!.month, true)}:{' '}
                    {formatMoney(trend[i]!.income, data.currency)}
                  </title>
                </rect>
                {i % 2 === trend.length % 2 ? null : (
                  <text
                    x={p.x}
                    y={H + 16}
                    textAnchor="middle"
                    className="fill-muted-foreground text-[10px]"
                  >
                    {monthLabel(trend[i]!.month)}
                  </text>
                )}
              </g>
            ))}
          </svg>
          <TableToggle
            rows={trend.map((x) => [
              monthLabel(x.month, true),
              formatMoney(x.income, data.currency),
            ])}
          />
        </div>
      )}
    </DashCard>
  );
}

/** The figures behind a chart, for screen readers and anyone who wants the numbers. */
function TableToggle({ rows }: { rows: Array<[string, string]> }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1">
      <button
        type="button"
        className="text-xs text-primary hover:underline"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {open ? 'Hide figures' : 'Show figures'}
      </button>
      {open ? (
        <table className="mt-1 w-full text-xs">
          <tbody>
            {rows.map(([a, b]) => (
              <tr key={a}>
                <td className="py-0.5">{a}</td>
                <td className="tnum py-0.5 text-right">{b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

/** India clients: the latest month's GSTR-3B position (document 06 T6). */
function GstCard() {
  const months = useGstMonths();
  const m = months.data?.months.find((x) => x.documents > 0) ?? months.data?.months[0];
  return (
    <DashCard
      title="GST"
      to={m ? '/taxes/gst/gstr3b' : '/taxes/gst'}
      {...(m ? { search: { period: m.period } } : {})}
      linkLabel={m ? 'View GSTR-3B' : 'Go to GST'}
      testId="card-gst"
    >
      {!months.data ? (
        <Loading />
      ) : !m ? (
        <p className="text-muted-foreground">No GST activity this financial year.</p>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">
            {gstMonthLabel(m.period)} · cash payable after input tax credit
          </div>
          <div className="tnum text-2xl font-semibold" data-testid="gst-cash">
            {homeCurrency()} {formatAmount(m.cashPayable)}
          </div>
          <dl className="grid grid-cols-2 gap-1 text-xs">
            <dt className="text-muted-foreground">Output tax</dt>
            <dd className="tnum text-right">{formatAmount(m.outputTax)}</dd>
            <dt className="text-muted-foreground">Input tax credit</dt>
            <dd className="tnum text-right">{formatAmount(m.itc)}</dd>
            <dt className="text-muted-foreground">GSTR-1 due</dt>
            <dd className="text-right">{formatDate(m.gstr1Due)}</dd>
            <dt className="text-muted-foreground">GSTR-3B due</dt>
            <dd className="text-right">{formatDate(m.gstr3bDue)}</dd>
          </dl>
        </div>
      )}
    </DashCard>
  );
}

function VatCard({ today, vatRegistered }: { today: string; vatRegistered: boolean }) {
  const returns = useVatReturns();
  const r = returns.data ? currentVatReturn(returns.data) : null;
  if (!vatRegistered)
    return (
      <DashCard title="VAT" to="/settings/company" linkLabel="Company settings" testId="card-vat">
        <p className="text-muted-foreground">
          This company is not VAT registered. Add its TRN in Company settings to prepare returns.
        </p>
      </DashCard>
    );
  const chip = r ? vatStatusChip(r, today) : null;
  return (
    <DashCard
      title="VAT"
      to={r ? `/taxes/vat/returns/${r.key}` : '/taxes/vat'}
      linkLabel={r ? (r.status === 'open' ? 'View return' : 'Prepare return') : 'Go to VAT'}
      testId="card-vat"
    >
      {!returns.data ? (
        <Loading />
      ) : !r ? (
        <p className="text-muted-foreground">Every VAT return is filed and paid.</p>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{r.label}</span>
            {chip ? (
              <span className={cn('rounded-full border px-2 text-xs', TONE_CLASS[chip.tone])}>
                {chip.label}
              </span>
            ) : null}
          </div>
          <div>
            <div className="text-xs text-muted-foreground">
              {r.status === 'open' ? 'Net VAT so far' : 'Net VAT'}
              {r.netVat.startsWith('-') ? ' (refundable)' : ' (payable)'}
            </div>
            <div className="tnum text-2xl font-semibold" data-testid="vat-net">
              {formatMoney(r.netVat, 'AED')}
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-1 text-xs">
            <dt className="text-muted-foreground">VAT on sales</dt>
            <dd className="tnum text-right">{formatAmount(r.outputVat)}</dd>
            <dt className="text-muted-foreground">VAT on purchases</dt>
            <dd className="tnum text-right">{formatAmount(r.inputVat)}</dd>
            <dt className="text-muted-foreground">Due date</dt>
            <dd className="text-right">{formatDate(r.dueDate)}</dd>
          </dl>
          {returns.data.filter((x) => x.status === 'overdue').length > 1 ? (
            <p className="text-xs text-destructive">
              {returns.data.filter((x) => x.status === 'overdue').length} returns are overdue.
            </p>
          ) : null}
        </div>
      )}
    </DashCard>
  );
}

function TasksCard({ data, isStaff }: { data: DashboardData | undefined; isStaff: boolean }) {
  const tasks = data ? visibleTasks(data.tasks, isStaff) : [];
  return (
    <DashCard
      title="Tasks"
      to="/accounting/periods"
      linkLabel="Periods and close"
      testId="card-tasks"
      className="xl:col-span-2"
    >
      {!data ? (
        <Loading />
      ) : tasks.length === 0 ? (
        <p className="flex items-center gap-2 text-muted-foreground">
          <CircleCheck className="size-4 text-[#1E7F4F]" /> Nothing waiting. The books are up to
          date.
        </p>
      ) : (
        <ul className="divide-y">
          {tasks.map((t) => {
            const v = taskView(t)!;
            return (
              <li key={t.key} className="flex items-center justify-between gap-2 py-2">
                <div>
                  <Link
                    to={v.to as never}
                    {...(v.search ? { search: v.search as never } : {})}
                    className="font-medium text-primary hover:underline"
                    data-testid={`task-${t.key}`}
                  >
                    {v.label}
                  </Link>
                  {t.amount && t.amount !== '0.00' ? (
                    <div className="tnum text-xs text-muted-foreground">
                      {formatMoney(t.amount, data.currency)}
                    </div>
                  ) : null}
                  {t.through ? (
                    <div className="text-xs text-muted-foreground">
                      Through {formatDate(t.through)}
                    </div>
                  ) : null}
                </div>
                <span className="tnum rounded-full bg-muted px-2 text-xs">{t.count}</span>
              </li>
            );
          })}
        </ul>
      )}
    </DashCard>
  );
}
